import { ErrorOutline, KeyboardArrowDown } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  InputBase,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import ForwardNumberUserField from "../reusables/ForwardNumberUserField";

const UpdateUserModal = ({
  open,
  setOpen,
  users,
  setUsers,
  user_data,
  fetchUsers,
  setOpenSnackbar,
  snackMsg,
  sortedRows,
  setSnackMsg,
}) => {
  function splitName(fullName) {
    if (!fullName) return { firstName: "", lastName: "" };

    const [firstName, ...rest] = fullName.trim().split(" ");
    const lastName = rest.join(" ");

    return { firstName, lastName };
  }

  const [user, setUser] = useState({
    type: user_data?.userType ?? user_data?.type,
    firstName: "",
    lastName: user_data?.lastName,
    email: user_data?.email,
    organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
    user_id: user_data?.user_id,
    status: user_data?.status,
    forwarding_number: user_data?.forwarding_number ?? "",
  });

  useEffect(() => {
    setUser({
      type: user_data?.userType ?? user_data?.type,
      firstName: splitName(user_data?.name).firstName,
      lastName: splitName(user_data?.name).lastName,
      email: user_data?.email,
      organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
      user_id: user_data?.user_id,
      status: user_data?.status,
      forwarding_number: user_data?.forwarding_number ?? "",
    });
  }, [user_data]);

  const [errorMsg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);

  const USER_TYPE_LABELS = {
    owner: "Owner - Organization owner (full access)",
    admin: "Admin - Full access to all features",
    user: "User - Can make calls and view data",
    viewer: "Viewer - Read access only",
  };

  const isOwner = user.type === "owner" || user_data?.userType === "owner";
  const ownerTypeTooltip =
    "The organization owner role cannot be changed directly. To transfer ownership, promote another user to owner first — the current owner will automatically become an admin.";

  const updateUser = async () => {
    setLoader(true);

    try {
      let user_data_ = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "update-user",
        {
          ...user,
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          updatingUserId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );
      setSnackMsg("user updated successfully");
      setOpenSnackbar(true);

      if (user_data_.data?.updatedItem) {
        localStorage.setItem(
          "organization",
          JSON.stringify(user_data_.data.updatedItem)
        );
      }

      setOpen(false);

      setUsers((prevUsers) =>
        prevUsers.map((existingUser) =>
          existingUser.user_id === user_data.user_id
            ? {
                ...existingUser,
                userType: user.type,
                status: user.status,
                name: user.firstName + " " + user.lastName,
                fName: user.firstName,
                lName: user.lastName,
                email: user.email,
                forwarding_number: user.forwarding_number || null,
              }
            : existingUser
        )
      );

      setLoader(false);
      setOpen(false);
    } catch (error) {
      if (error.response && error.response.data) {
        setSnackMsg(error.response.data.error);
        setOpenSnackbar(true);
      }
      setLoader(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth={false}
        maxWidth="sm"
        BackdropProps={{
          style: { backgroundColor: "rgb(144,150,160,0.8)" },
        }}
      >
        <div className="px-6 py-6">
          {errorMsg && (
            <div className="text-center text-red-500 text-sm font-semibold mb-3">
              <ErrorOutline /> {errorMsg}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-[18px] font-semibold mb-5">Edit User</div>
            <X
              onClick={() => setOpen(false)}
              className="text-gray-400 cursor-pointer"
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium block mb-1">User Type</label>
            <Tooltip
              title={isOwner ? ownerTypeTooltip : ""}
              placement="top"
              arrow
            >
              <span className="block w-full">
                <Select
                  IconComponent={KeyboardArrowDown}
                  fullWidth
                  value={user.type || ""}
                  onChange={(e) => setUser({ ...user, type: e.target.value })}
                  displayEmpty
                  disabled={isOwner}
                  renderValue={(selected) =>
                    USER_TYPE_LABELS[selected] || selected || "Select user type"
                  }
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #D1D5DB",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiSelect-select": {
                      padding: "12px",
                      fontSize: "14px",
                    },
                  }}
                >
                  {isOwner && (
                    <MenuItem value="owner">{USER_TYPE_LABELS.owner}</MenuItem>
                  )}
                  <MenuItem value="admin">
                    {USER_TYPE_LABELS.admin}
                  </MenuItem>
                  <MenuItem value="user">
                    {USER_TYPE_LABELS.user}
                  </MenuItem>
                  <MenuItem value="viewer">{USER_TYPE_LABELS.viewer}</MenuItem>
                </Select>
              </span>
            </Tooltip>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium block mb-1">First Name</label>
              <InputBase
                fullWidth
                value={user.firstName}
                onChange={(e) => setUser({ ...user, firstName: e.target.value })}
                sx={{
                  bgcolor: "white",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  px: 2,
                  py: 1.2,
                  fontSize: "14px",
                }}
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Last Name</label>
              <InputBase
                fullWidth
                value={user.lastName}
                onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                sx={{
                  bgcolor: "white",
                  border: "1px solid #D1D5DB",
                  borderRadius: "6px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  px: 2,
                  py: 1.2,
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium block mb-1">
              Email Address
            </label>
            <InputBase
              fullWidth
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              disabled
              sx={{
                bgcolor: "white",
                border: "1px solid #D1D5DB",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                px: 2,
                py: 1.2,
                fontSize: "14px",
              }}
            />
          </div>

          <ForwardNumberUserField
            value={user.forwarding_number}
            onChange={(forwarding_number) =>
              setUser((prev) => ({ ...prev, forwarding_number }))
            }
          />

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              sx={{
                textTransform: "none",
                color: "#374151",
                bgcolor: "#ffffff",
                border: "1px solid #E5E6EB",
                px: 4,
                py: 1,
                borderRadius: "8px",
              }}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              sx={{
                px: 1,
                py: 1,
                borderRadius: "8px",
                fontWeight: 530,
              }}
              onClick={updateUser}
              disabled={loader}
            >
              {loader ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default UpdateUserModal;
